<script setup lang="ts">
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { postColorTailwindClass } from '@@/bits/post_color'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import type { Post, PostColor, VueCssClass } from '@@/types'
import { usePostActionMenuTrigger } from '@@/vuecomposables/usePostActionMenuTrigger'
import emitClickOnTouch from '@@/vuedirectives/emit_click_on_touch'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const vEmitClickOnTouch = emitClickOnTouch
const isTouchable = device.touchable

const props = withDefaults(
  defineProps<{
    post: Post
    backgroundColor: PostColor
    colorScheme: 'light' | 'dark'
    xOverlay?: boolean
    /** This prop dictates whether absolutely position filler elements should be shown */
    xFillerElements?: boolean
    /** This prop dictates whether the more actions button should have additional classes */
    moreActionsButtonClasses?: VueCssClass
  }>(),
  {
    xOverlay: true,
    xFillerElements: true,
    moreActionsButtonClasses: undefined,
  },
)

defineEmits<{
  (name: 'edit'): void
}>()

const { isStream, isTimeline, isMap, isScreenshotMode, user, isFrozen, isPostSizeWide } = storeToRefs(useSurfaceStore())
const { canIEdit, canIWrite } = storeToRefs(useSurfacePermissionsStore())
const { isWishTranslatedOrBeingTranslated } = useSurfaceTranslationsStore()

const isEditButtonFocused = ref<boolean>(false)
const isMoreActionsButtonFocused = ref<boolean>(false)

/**
 * Only for timeline: the whole post can be scrolled.
 */
const isWishContentScrollable = computed((): boolean => {
  return isTimeline.value || isMap.value
})

const isPostAuthor = computed<boolean>(() => {
  return props.post.author_id === user.value?.id
})

const isTranslatedWishIconShown = computed<boolean>(() => {
  return isWishTranslatedOrBeingTranslated(props.post.id)
})

const xEditPost = computed<boolean>(() => {
  if (isFrozen.value || isTouchable || isTranslatedWishIconShown.value) return false
  return canIEdit.value || (canIWrite.value && isPostAuthor.value)
})

const isLightColorScheme = computed<boolean>(() => {
  return props.colorScheme === 'light'
})

const bgColorClass = computed<string>(() => {
  return postColorTailwindClass({ postColor: props.backgroundColor, isLightColorScheme: isLightColorScheme.value })
})

const cornerColorClass = computed<string>(() => {
  switch (props.backgroundColor) {
    case 'red':
      return isLightColorScheme.value ? 'corner-scarlet-100' : 'corner-scarlet-900'
    case 'orange':
      return isLightColorScheme.value ? 'corner-canary-100' : 'corner-tangerine-900'
    case 'green':
      return isLightColorScheme.value ? 'corner-park-100' : 'corner-park-900'
    case 'blue':
      return isLightColorScheme.value ? 'corner-oceanic-100' : 'corner-oceanic-900'
    case 'purple':
      return isLightColorScheme.value ? 'corner-grape-100' : 'corner-grape-900'
    default:
      return isLightColorScheme.value ? 'corner-light-ui-100' : 'corner-dark-ui-100'
  }
})

const { showPostActionMenuWithTriggerRect } = usePostActionMenuTrigger()

function onMoreActionsButtonClick(event: Event): void {
  const button = event.currentTarget as HTMLButtonElement
  showPostActionMenuWithTriggerRect(button, props.post)
}
</script>

<template>
  <!-- webkit-mask-image is to help with a bug in Safari with border-radius + overflow + transform: https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b -->
  <div
    v-if="!isScreenshotMode"
    :class="{
      absolute: true,
      'z-10': true,
      'top-0 end-0': true,
      group: true,
      'rounded-se-xl': true,
    }"
  >
    <div>
      <!-- Wrapper for the whole block -->
      <div
        :class="{
          'h-[29px] w-min': true,
          relative: true,
          flex: true,
          'ps-1': true,
          'overflow-visible': isTouchable,
        }"
      >
        <!-- Sliding div for Edit button and the sliding corner  -->
        <div
          v-if="xEditPost"
          :class="{
            'transition-transform': true,
            'translate-s-0': isEditButtonFocused,
            'translate-s-full group-hover:translate-s-0': !isEditButtonFocused,
            'rounded-es-lg': true,
            'pe-[9px]': true,
            'pt-[3px] me-[-8px]': true,
            'flex justify-start items-start': true,
            [bgColorClass]: true,
          }"
        >
          <!-- New corner after the animation  -->
          <!-- For wide posts, top + 2px so that the corner is not cut off by the increased size of the border -->
          <div
            v-if="xOverlay && !isWishContentScrollable"
            :class="{
              absolute: true,
              'start-[-4px] top-[7px]': !isStream && !isPostSizeWide,
              'start-[-4px] top-[9px]': !isStream && isPostSizeWide,
              'start-[-4px] top-[11px]': isStream && !isPostSizeWide,
              'start-[-4px] top-[13px]': isStream && isPostSizeWide,
              'rtl:-scale-x-100': true,
              [cornerColorClass]: true,
            }"
          />
          <!-- The edit button itself  -->
          <OzPlainButton
            :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
            :dark-mode="colorScheme === 'dark'"
            :size-preset="null"
            :class="[
              'font-semibold',
              'text-body-extra-small',
              'py-0',
              'px-1.5',
              'h-4',
              'mt-[5px]',
              'mb-[5px]',
              // Focus
              colorScheme === 'light' && 'focus-visible:text-grape-500',
              colorScheme === 'dark' && 'focus-visible:text-canary-500',
            ]"
            :text="__('Edit')"
            :aria-label="__('Edit this post')"
            data-testid="surfacePostEditButton"
            @click="$emit('edit')"
            @focus="isEditButtonFocused = true"
            @blur="isEditButtonFocused = false"
          />
        </div>
        <!-- Divider between edit and 3 dots button  -->
        <div
          v-if="xEditPost"
          :class="{
            'z-20': true,
            'opacity-0': !xFillerElements || !isEditButtonFocused || isMoreActionsButtonFocused,
            'opacity-100': xFillerElements && isEditButtonFocused,
            'group-hover:opacity-100': xFillerElements && !isEditButtonFocused && !isMoreActionsButtonFocused,
            'transition-opacity': true,
            'h-4 mt-[8px] mb-[5px]': true,
            'w-[0px]': true,
            'border-e-[0.5px] border-solid': true,
            'border-dark-text-400': colorScheme === 'light',
            'border-light-text-400': colorScheme === 'dark',
          }"
        ></div>
        <!-- Div for the 3 dots and the static corner   -->
        <!-- 22 + 1 px for stream+standard and 22 + 3 px for stream+wideposts to avoid pixel protruding -->
        <div
          :class="{
            'w-5.5': !isStream,
            'w-[23px]': isStream && !isPostSizeWide,
            'w-[25px]': isStream && isPostSizeWide,
            'pt-[3px] h-[26px]': true,
            flex: true,
            relative: true,
            'overflow-visible': true,
            'justify-end': true,
            'rounded-es-lg': true,
            [bgColorClass]: true,
          }"
        >
          <!-- Static corner, hides when sliding corner is used -->
          <div
            v-if="xOverlay && !isWishContentScrollable"
            :class="[
              {
                absolute: true,
                'start-[-4px] top-[7px]': !isStream && !isPostSizeWide,
                'start-[-4px] top-[9px]': !isStream && isPostSizeWide,
                'start-[-4px] top-[11px]': isStream && !isPostSizeWide,
                'start-[-4px] top-[13px]': isStream && isPostSizeWide,
                'rtl:-scale-x-100': true,
                [cornerColorClass]: true,
              },
              {
                'opacity-0': !xFillerElements || (xEditPost && isEditButtonFocused) || isMoreActionsButtonFocused,
                'transition opacity-100 group-hover:opacity-0':
                  xFillerElements && xEditPost && !isEditButtonFocused && !isMoreActionsButtonFocused,
              },
            ]"
          />
          <!-- 3 dots button -->
          <OzPlainButton
            v-emit-click-on-touch
            :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
            :dark-mode="colorScheme === 'dark'"
            :size-preset="null"
            :class="[
              'post-actions-button',
              'shrink-0',

              // Want: 10px between 3 dots at the edge.
              // Assume 3 dots width is 2px.
              // DistanceFromEdge - (IconWidth - 3DotsWidth) / 2 = 10px - (16px - 2px) / 2 = 10px - 7px = 3px

              !isTouchable && [
                // ContainerHeight - ContainerMargin = 29px - 3px = 26px
                'h-[26px]',
                {
                  'w-5.5': !isStream,
                  'w-[23px]': isStream && !isPostSizeWide,
                  'w-[25px]': isStream && isPostSizeWide,
                },
                'py-0',
                'px-[3px]',
              ],
              isTouchable && [
                'w-[48px]',
                // 42px is the minimum height of the post, hence maximum height of the button.
                'h-[42px]',
                // Total = 48px - 16px = 32px
                'ps-[29px] pe-[3px]',
                // Total = 42px - 16px - 3px = 23px. We have to omit the top padding.
                'pt-[3px] pb-[20px]',
              ],
              // Focus
              colorScheme === 'light' && 'focus-visible:text-grape-500 focus-visible:border-grape',
              colorScheme === 'dark' && 'focus-visible:text-canary-500 focus-visible:border-canary',
              'border-2 focus-visible:border-solid border-transparent rounded-lg',
              'flex items-center justify-center',
              moreActionsButtonClasses,
            ]"
            :aria-label="__('More post actions')"
            :title="__('More post actions')"
            data-testid="surfacePostMoreActionsButton"
            @click="onMoreActionsButtonClick"
            @focus="isMoreActionsButtonFocused = true"
            @blur="isMoreActionsButtonFocused = false"
          >
            <OzIcon name="more_vertical" :size="16" />
          </OzPlainButton>
        </div>
      </div>
      <!-- Bottom corner -->
      <div
        v-if="xOverlay && !isWishContentScrollable"
        :class="[
          {
            absolute: true,
            'bottom-[-4px] end-[7px]': !isStream && !isPostSizeWide,
            'bottom-[-4px] end-[9px]': !isStream && isPostSizeWide,
            'bottom-[-4px] end-[11px]': isStream && !isPostSizeWide,
            'bottom-[-4px] end-[13px]': isStream && isPostSizeWide,
            'rtl:-scale-x-100': true,
            [cornerColorClass]: true,
            'opacity-0': !xFillerElements || isMoreActionsButtonFocused,
          },
        ]"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin corner($color) {
  width: 5px;
  height: 5px;
  background: radial-gradient(circle at bottom left, transparent 4px, $color 4.1px);
}
.corner-light-ui-100 {
  @include corner(theme('colors.light-ui.100'));
}
.corner-dark-ui-100 {
  @include corner(theme('colors.dark-ui.100'));
}
.corner-scarlet-100 {
  @include corner(theme('colors.scarlet.100'));
}
.corner-scarlet-900 {
  @include corner(theme('colors.scarlet.900'));
}
.corner-canary-100 {
  @include corner(theme('colors.canary.100'));
}
.corner-tangerine-900 {
  @include corner(theme('colors.tangerine.900'));
}
.corner-park-100 {
  @include corner(theme('colors.park.100'));
}
.corner-park-900 {
  @include corner(theme('colors.park.900'));
}
.corner-oceanic-100 {
  @include corner(theme('colors.oceanic.100'));
}
.corner-oceanic-900 {
  @include corner(theme('colors.oceanic.900'));
}
.corner-grape-100 {
  @include corner(theme('colors.grape.100'));
}
.corner-grape-900 {
  @include corner(theme('colors.grape.900'));
}
</style>
